import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-tab-set.css?inline';
import '../pk-tab/pk-tab.js';

/**
 * `<pk-tab-set>` — tab container with light-DOM `<pk-tab>` children.
 *
 * Slot the `<pk-tab>` elements as the default slot. The container tracks the
 * `active` attribute (the current href) and:
 *
 *   1. Sets `active` on the matching child `<pk-tab>` on connect + on
 *      attribute change.
 *   2. Intercepts tab clicks, calls `preventDefault()` (so the host can route
 *      via the SPA / Razor partial-update path), and fires `pk-tab-change`
 *      with `{ href }`.
 *
 * The intercept is a no-op if the click came from a disabled tab.
 *
 * Attributes:
 *   - `active` — the href of the currently-active tab.
 *
 * Events:
 *   - `pk-tab-change` — `CustomEvent<{ href: string }>` fired when the user
 *                       clicks a non-disabled tab. The consumer decides
 *                       whether to navigate (Razor) or swap content (SPA).
 *
 * Slots:
 *   - default — `<pk-tab>` children.
 */
export class PkTabSet extends HTMLElement {
    static readonly tagName = 'pk-tab-set';

    static get observedAttributes(): string[] {
        return ['active'];
    }

    private readonly _root: ShadowRoot;
    private _slotChangeBound = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this.addEventListener('click', this._onClick);
    }

    disconnectedCallback(): void {
        this.removeEventListener('click', this._onClick);
    }

    attributeChangedCallback(): void {
        this._syncActive();
        this.render();
    }

    private readonly _onClick = (e: MouseEvent): void => {
        const path = e.composedPath();
        for (const target of path) {
            if (target instanceof Element && target.tagName === 'PK-TAB') {
                if (target.hasAttribute('disabled')) return;
                const href = target.getAttribute('href') ?? '';
                // Suppress the default anchor navigation so the consumer can
                // handle routing themselves.
                e.preventDefault();
                this.setAttribute('active', href);
                this.dispatchEvent(new CustomEvent('pk-tab-change', {
                    detail:   { href },
                    bubbles:  true,
                    composed: true,
                }));
                return;
            }
        }
    };

    private _syncActive(): void {
        const active = this.getAttribute('active') ?? '';
        const tabs = this.querySelectorAll(':scope > pk-tab');
        tabs.forEach((tab) => {
            if (tab.getAttribute('href') === active) {
                tab.setAttribute('active', '');
            } else {
                tab.removeAttribute('active');
            }
        });
    }

    private template(): TemplateResult {
        return html`
            <style>${styles}</style>
            <div class="tab-list" role="tablist">
                <slot @slotchange=${this._onSlotChange}></slot>
            </div>
        `;
    }

    private readonly _onSlotChange = (): void => {
        // Sync `active` whenever the slot contents change so late-added or
        // server-rendered <pk-tab> children pick up the right state on first
        // paint.
        this._syncActive();
        this._slotChangeBound = true;
    };

    private render(): void {
        render(this.template(), this._root);
        // Run an extra sync after the first render in case slotchange hasn't
        // fired yet (jsdom's lifecycle ordering vs real browser).
        if (!this._slotChangeBound) this._syncActive();
    }
}

if (!customElements.get(PkTabSet.tagName)) {
    customElements.define(PkTabSet.tagName, PkTabSet);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-tab-set': PkTabSet;
    }
}
